/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import type {
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import { spacingSets } from "@/tokens/configs/spacing_config";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { RichText } from "@/ui/molecules/rich_text";
import { FaqSection } from "@/ui/organisms/faq_section";
import { TextFeature } from "@/ui/organisms/text_feature";
import { PlanMatrix } from "@/ui/wrappers/plan_matrix";
import { PricingHero } from "@/ui/wrappers/pricing_hero";

import { generatePageLevelProps } from "@/util/data_util";
import { generateLegacyPreFooterData } from "@/util/strapi_data_util";
import { getGlobals, getPricingPage } from "@/util/strapi_util";
import { buildStylesByBreakpoint } from "@/util/style_util";

export const getStaticProps = async ({
    preview: previewMode = false,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = "pricing";

    const globalsData = await getGlobals(previewMode);
    const pageData = await getPricingPage(previewMode);

    return {
        props: {
            ...generatePageLevelProps({ path: `/${slug}`, previewMode, slug }),
            ...generateLegacyPreFooterData(globalsData, pageData.Pre_Footer),
            globalsData,
            pageData,
        },
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    /**
     * Styles
     */
    const containerStyles = css(
        {
            display: "flex",
            flexDirection: "column",
        },
        buildStylesByBreakpoint("rowGap", spacingSets.DefaultSectionMargin),
    );

    /**
     * Rendering
     */
    const renderFaqSection = () => {
        return (
            <div>
                {/* Casting pageData.FAQs since we only call renderFaqSection if it exists */}
                <FaqSection {...props.pageData.FAQs!} />

                {props.pageData.FAQ_Footer && (
                    <GridColumn
                        className={css({
                            textAlign: "center",
                        })}
                        columnSpan={6}
                        columnStart={{
                            extraSmall: 1,
                            medium: 4,
                        }}
                        marginTop="PricingParagraphMargin"
                    >
                        <RichText
                            className={css({
                                "*>a": { display: "inline-block" },
                            })}
                            config="Longform"
                        >
                            {props.pageData.FAQ_Footer}
                        </RichText>
                    </GridColumn>
                )}
            </div>
        );
    };

    return (
        <div css={containerStyles}>
            <PricingHero {...props.pageData} />

            <Divider />

            {props.pageData.Privacy && (
                <TextFeature {...props.pageData.Privacy} />
            )}

            <Divider />

            <PlanMatrix {...props.pageData} />

            <Divider />

            {props.pageData.FAQs && renderFaqSection()}
        </div>
    );
};

export default Page;
